import { useCallback, useEffect, useMemo, useState } from 'react';
import { authApi } from '../api/auth.js';
import { ApiError } from '../api/client.js';
import { offlineStore } from '../offline/offline-store.js';
import { AuthContext } from './useAuth.js';

/**
 * Estado de sesión de la aplicación.
 *
 * `estado` tiene TRES valores y no dos, y el tercero es el importante:
 *
 *   · 'cargando'   todavía no se sabe si hay sesión.
 *   · 'autenticado' hay cookie válida y `usuario` está relleno.
 *   · 'anonimo'    no hay sesión.
 *
 * Mientras vale 'cargando', la interfaz NO debe decidir a dónde llevar al
 * usuario. Si se tratara como anónimo durante el arranque, cada recarga
 * expulsaría a un usuario que sí ha iniciado sesión: parpadea la pantalla de
 * login, el usuario cree que le han cerrado la cuenta y probablemente entre su
 * contraseña otra vez.
 *
 * Por eso las rutas privadas esperan a `cargando` antes de decidir.
 */

/** @typedef {'cargando' | 'autenticado' | 'anonimo'} EstadoSesion */

/** @param {{ children: React.ReactNode }} props */
export function AuthProvider({ children }) {
  /** @type {import('react').useState<import('@academy/shared').PublicUser | null>} */
  const [usuario, setUsuario] = useState(() =>
    offlineStore.isGuest() ? offlineStore.getGuestUser() : null,
  );
  const [estado, setEstado] = useState(
    () => (offlineStore.isGuest() ? /** @type {EstadoSesion} */ ('autenticado') : 'cargando'),
  );

  /**
   * Aviso de seguridad que hay que mostrar una vez.
   *
   * Guarda el mensaje de `SESSION_REVOKED`: alguien reutilizó un refresh token
   * revocado, así que el servidor cerró todas las sesiones. Es el único aviso
   * que se recuerda aunque el usuario navegue, porque es la explicación de por
   * qué de repente está deslogueado.
   */
  const [aviso, setAviso] = useState(/** @type {string | null} */ (null));

  /** Traduce un fallo de red en el estado que corresponde. */
  const manejarError = useCallback((error) => {
    if (error instanceof ApiError && error.code === 'SESSION_REVOKED') {
      setUsuario(null);
      setEstado('anonimo');
      setAviso(error.message);
      return;
    }

    // Cualquier otro 401 es simplemente "no hay sesión". Un 0 es "no se pudo
    // contactar con el servidor": se trata igual para no bloquear la app, pero
    // sin inventarse un mensaje de sesión.
    if (error instanceof ApiError && (error.status === 401 || error.status === 0)) {
      setUsuario(null);
      setEstado('anonimo');
      return;
    }

    throw error;
  }, []);

  // Al montar, se pregunta al servidor quién es el usuario. `api.me()` ya
  // reintenta con un refresh si el access token caducó, así que aquí no hace
  // falta orquestar nada: basta con dejar que el cliente haga su trabajo.
  useEffect(() => {
    let vigente = true;

    if (offlineStore.isGuest()) return undefined;

    authApi
      .me()
      .then((user) => {
        if (!vigente) return;
        if (user) {
          setUsuario(user);
          setEstado('autenticado');
        } else {
          setEstado('anonimo');
        }
      })
      .catch((error) => {
        if (!vigente) return;
        try {
          manejarError(error);
        } catch (inesperado) {
          console.error('No se pudo comprobar la sesión', inesperado);
          setEstado('anonimo');
        }
      });

    return () => {
      vigente = false;
    };
  }, [manejarError]);

  const loginAsGuest = useCallback(() => {
    offlineStore.setGuest(true);
    const guest = offlineStore.getGuestUser();
    setUsuario(guest);
    setEstado('autenticado');
    setAviso(null);
    return guest;
  }, []);

  const login = useCallback(async (credenciales) => {
    offlineStore.setGuest(false);
    const user = await authApi.login(credenciales);
    setUsuario(user);
    setEstado('autenticado');
    setAviso(null);
    return user;
  }, []);

  const register = useCallback(async (datos) => {
    offlineStore.setGuest(false);
    const user = await authApi.register(datos);
    setUsuario(user);
    setEstado('autenticado');
    setAviso(null);
    return user;
  }, []);

  const logout = useCallback(async () => {
    try {
      if (offlineStore.isGuest()) {
        offlineStore.setGuest(false);
      } else {
        await authApi.logout();
      }
    } finally {
      offlineStore.setGuest(false);
      setUsuario(null);
      setEstado('anonimo');
    }
  }, []);

  const logoutEverywhere = useCallback(async () => {
    await authApi.logoutEverywhere();
    setUsuario(null);
    setEstado('anonimo');
  }, []);

  const updateProfile = useCallback(async (cambios) => {
    const user = await authApi.updateProfile(cambios);
    setUsuario(user);
    return user;
  }, []);

  const changePassword = useCallback((cambios) => authApi.changePassword(cambios), []);

  const deleteAccount = useCallback(async () => {
    await authApi.deleteAccount();
    setUsuario(null);
    setEstado('anonimo');
  }, []);

  const dismissAviso = useCallback(() => setAviso(null), []);

  const isGuest = offlineStore.isGuest();

  const valor = useMemo(
    () => ({
      usuario,
      estado,
      aviso,
      isGuest,
      loginAsGuest,
      login,
      register,
      logout,
      logoutEverywhere,
      updateProfile,
      changePassword,
      deleteAccount,
      dismissAviso,
    }),
    [
      usuario,
      estado,
      aviso,
      isGuest,
      loginAsGuest,
      login,
      register,
      logout,
      logoutEverywhere,
      updateProfile,
      changePassword,
      deleteAccount,
      dismissAviso,
    ],
  );

  return <AuthContext.Provider value={valor}>{children}</AuthContext.Provider>;
}
